<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import DateRange from '@/components/DateRange.vue'
import TrophyIcon from '@/components/TrophyIcon.vue'

/**
 * One trophy tile: the cup, then event, prize and dates one under the other.
 *
 * It takes a single award in the shape built by App\Libraries\EventAwardLib and resolves its texts
 * itself, so the caller (the cabinet) only has to list the awards it wants to show.
 */
const props = defineProps({
  /** Award in the shape built by App\Libraries\EventAwardLib. */
  award: { type: Object, required: true },
  /** Name the winner of the award: needed by the general hall of fame, not by a player page. */
  showPlayer: { type: Boolean, default: false },
})

const { t } = useI18n()

/**
 * Labels of the podium places: the colour comes from the shared podium palette (see lib/podium).
 * The other award types (fastest lap, participation, ...) will add their entry here next to the
 * ones below, keyed by the same `awardKey` the server writes.
 */
const PODIUM_LABELS = {
  1: 'awards.podium.first',
  2: 'awards.podium.second',
  3: 'awards.podium.third'
}

/** What was won: a podium place today, a plain prize for award types not known yet. */
const prize = computed(() => t(
  (props.award.awardKey === 'podium' ? PODIUM_LABELS[props.award.place] : null)
    ?? `awards.${props.award.awardKey}`,
))

/** Events have no title of their own: the type is the name, the dates say when it was raced. */
const label = computed(() => props.award.event?.eventType
  ? t(`eventTypes.${props.award.event.eventType}`)
  : `#${props.award.event?.id ?? '?'}`)

/** Each tile opens the event the award was won in; without an event it stays a plain card. */
const to = computed(() => props.award.event?.id
  ? { name: 'event_detail', params: { id: props.award.event.id } }
  : null)
</script>

<template>
  <v-card
    :to="to"
    hover
    variant="outlined"
    rounded="lg"
    class="trophy border-0"
  >
    <TrophyIcon :award="award" />

    <span class="trophy__event">{{ label }}</span>
    <span class="trophy__prize">{{ prize }}</span>
    <DateRange class="trophy__date" :from="award.event?.dateStart" :to="award.event?.dateEnd" />

    <!--
      The player name stays plain text: the whole tile is already a link to the event, and a link
      inside a link is not allowed.
    -->
    <span v-if="showPlayer && award.player" class="trophy__player">
      <v-icon size="13">mdi-account</v-icon>
      {{ award.player.name }}
    </span>
  </v-card>
</template>

<style scoped>
/* One trophy: the cup on top, then event, prize and dates one under the other. */
.trophy {
  flex: 0 1 172px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 12px 8px;
  text-align: center;
}

.trophy__event {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.15;
}

.trophy__prize {
  font-size: 0.72rem;
  opacity: 0.85;
}

.trophy__date {
  font-size: 0.68rem;
  opacity: 0.6;
}

.trophy__player {
  font-size: 0.7rem;
  opacity: 0.8;
}
</style>
